import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '@/shared/api/errors';

import { quotesRootKey } from '@/shared/quotes/api';

import {
  emailDetailKey,
  emailListsKey,
  emailNeighboursKey,
  emailViewsKey,
  sharedEmailsApi,
} from './api';
import { liveDecisionOf, withLiveFields } from './live';
import type { Email, EmailDetail, TriageCorrection, TriageDecision } from './types';

/** How often the detail is re-read while a confirmed action is on its way to the worker. */
export const ACTION_POLL_MS = 3_000;

/**
 * After this long with `action_requested_at` set and no answer, the screen stops waiting
 * and offers Reintentar: a worker that has not answered in two minutes is not about to.
 */
export const ACTION_WAIT_MS = 2 * 60_000;

/**
 * The worker's answer to the **last** request, or null while it has not answered it.
 *
 * `acted_at` stays from an earlier action: the copy an `auto` label made days ago, or the
 * first «Correcto» before «Devolver a la bandeja». The back clears `action_error` when a
 * person asks again but not `acted_at`, so an `acted_at` older than `action_requested_at`
 * answers a previous request, not this one. Read as the answer, it stopped the poll the
 * moment «Correcto» was pressed and the screen said «sigue en la bandeja de entrada»
 * while the mail was already in its folder (preview, 2026-09-25).
 */
export function actedSinceRequest(decision: {
  action_requested_at?: string | null;
  acted_at?: string | null;
}): string | null {
  const acted = decision.acted_at ?? null;
  const requested = decision.action_requested_at ?? null;
  if (!acted || !requested) return acted;
  return Date.parse(acted) >= Date.parse(requested) ? acted : null;
}

/**
 * Whether the live decision of an email is between "a person asked" and "the worker
 * answered" (contract v10): `action_requested_at` set, no `acted_at` since then
 * (`actedSinceRequest`), nothing in `action_error`, and not older than `ACTION_WAIT_MS`.
 */
export function actionInFlight(email: EmailDetail | undefined, now = Date.now()): boolean {
  const decision = [...(email?.triage_decisions ?? [])].sort((a, b) =>
    a.created_at.localeCompare(b.created_at),
  );
  const live = decision.at(-1);
  if (!live?.action_requested_at || actedSinceRequest(live) || live.action_error) return false;
  return now - Date.parse(live.action_requested_at) < ACTION_WAIT_MS;
}

/** Between «Rescatar» and the worker's answer: `rescue_requested_at` set, nothing else. */
export function rescueInFlight(email: EmailDetail | undefined, now = Date.now()): boolean {
  if (!email?.rescue_requested_at || email.rescued_at || email.rescue_error) return false;
  return now - Date.parse(email.rescue_requested_at) < ACTION_WAIT_MS;
}

/** How often the detail is re-read while the worker learns from a note (contract v10.35). */
export const NOTE_POLL_MS = 3_000;

/**
 * How long the detail keeps asking what a note taught. The worker answers in a few
 * seconds; past a minute it is busy or down, its own sweep picks the note up later, and
 * the panel stays on «Aprendiendo de …» without asking every three seconds.
 */
export const NOTE_WAIT_MS = 60_000;

/**
 * Whether the note on the live decision is waiting for the worker (`note_learning.status`
 * `pendiente`) and was saved less than `NOTE_WAIT_MS` ago. Measured from `corrected_at`,
 * the moment the note was saved, as `actionInFlight` measures from `action_requested_at`.
 */
export function noteLearningPending(email: EmailDetail | undefined, now = Date.now()): boolean {
  if (email?.note_learning?.status !== 'pendiente') return false;
  const savedAt = liveDecisionOf(email.triage_decisions)?.corrected_at;
  if (!savedAt) return false;
  return now - Date.parse(savedAt) < NOTE_WAIT_MS;
}

export function useEmailDetail(id: string | undefined) {
  return useQuery<EmailDetail, ApiError>({
    queryKey: emailDetailKey(id ?? ''),
    queryFn: () => sharedEmailsApi.detail(id ?? ''),
    enabled: Boolean(id),
    // While a confirmed redirect is on its way, poll: the worker writes `acted_at` or
    // `action_error` and the screen has to move from «Reenviando…» to the outcome
    // without anybody pressing F5. The same while the worker learns from a note.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (actionInFlight(data) || rescueInFlight(data)) return ACTION_POLL_MS;
      return noteLearningPending(data) ? NOTE_POLL_MS : false;
    },
  });
}

/** «Rescatar»: the detail comes back with `rescue_requested_at` and the poll takes over. */
export function useRescue(id: string) {
  const queryClient = useQueryClient();
  return useMutation<EmailDetail, ApiError, void>({
    mutationFn: () => sharedEmailsApi.rescue(id),
    onSuccess: (detail) => {
      queryClient.setQueryData(emailDetailKey(id), detail);
      void queryClient.invalidateQueries({ queryKey: emailListsKey });
    },
  });
}

/**
 * Requeue one email. Everything under `['emails']` is refetched: the status changed,
 * and the inbox lists show it.
 */
export function useReprocess(id: string) {
  const queryClient = useQueryClient();
  return useMutation<Email, ApiError, void>({
    mutationFn: () => sharedEmailsApi.reprocess(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['emails'] });
    },
  });
}

/**
 * Confirm or correct the live decision of an email.
 *
 * The detail is patched from the answer instead of refetched: the endpoint returns the
 * decision it just wrote, so a second `GET` would only ask the API to repeat itself, and
 * `live_category`, `live_destination` and `reviewed` follow the patch with the back's own
 * rule (`withLiveFields`). The lists are invalidated because the inbox paints the
 * decision on every row. A corrected **category** is the exception (contract v10.24): to
 * or from «oferta» it creates or cancels a quote, so the detail (`quote_id`,
 * `quote_status`) and Presupuestos are read again.
 */
export function useCorrectTriage(emailId: string, options: { onSuccess?: () => void } = {}) {
  const queryClient = useQueryClient();
  return useMutation<TriageDecision, ApiError, TriageCorrection>({
    mutationFn: (correction) => sharedEmailsApi.correctTriage(emailId, correction),
    onSuccess: (decision, correction) => {
      let hadNote = false;
      queryClient.setQueryData<EmailDetail>(emailDetailKey(emailId), (previous) => {
        hadNote = Boolean(previous?.note_learning);
        return previous
          ? withLiveFields({ ...previous, triage_decisions: replaceDecision(previous, decision) })
          : previous;
      });
      void queryClient.invalidateQueries({ queryKey: emailListsKey });
      // Answered, it leaves «Por revisar» and lands in its folder (contract v10.25).
      void queryClient.invalidateQueries({ queryKey: emailViewsKey });
      void queryClient.invalidateQueries({ queryKey: emailNeighboursKey });
      if (correction.category) {
        void queryClient.invalidateQueries({ queryKey: emailDetailKey(emailId) });
        void queryClient.invalidateQueries({ queryKey: quotesRootKey });
      } else if (correction.note || hadNote) {
        // A note is what the platform learns from (contract v10.35): the detail comes back
        // with `note_learning` in `pendiente` and the poll above follows it. An answer
        // without a note after one with it takes what was learned away: read that too.
        void queryClient.invalidateQueries({ queryKey: emailDetailKey(emailId) });
      }
      options.onSuccess?.();
    },
  });
}

/** The decision list with `updated` in place of the row it replaces, or appended. */
function replaceDecision(email: EmailDetail, updated: TriageDecision): TriageDecision[] {
  const decisions = email.triage_decisions ?? [];
  const index = decisions.findIndex((decision) => decision.id === updated.id);
  if (index === -1) return [...decisions, updated];
  return decisions.map((decision, position) => (position === index ? updated : decision));
}
